/* attaching mouse events to label bc input is hidden */
/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { tokens } from '@companycam/slab';

import {
  HiddenInput,
  CheckboxRadioContainer,
  StyledCheckboxRadioLabel,
  StyledCheckboxRadioFacade,
} from './shared/styles';

/**
 * @deprecated Use { InputCheckbox } from `@companycam/slab-web` instead
 *
 * https://slab-web.cocam.co/?path=/docs/inputcheckbox--docs
 */

const CCCheckbox = ({
  checked,
  className,
  color,
  borderColor,
  checkedBackgroundColor,
  checkmarkColor,
  contentBefore,
  contentAfter,
  disabled,
  id,
  indeterminate,
  label,
  name,
  onClick,
  onFocus,
  onBlur,
  onMouseOver,
  onMouseOut,
  onChange,
  shape,
  size,
  style,
  value,
  ...props
}) => {
  const checkboxRef = useRef();
  const [isHovered, setIsHovered] = useState(false);
  const handleChange = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    onChange(e);
  };
  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    onClick(e);
  };
  const handleMouseOver = (e) => {
    setIsHovered(true);
    onMouseOver(e);
  };
  const handleMouseOut = (e) => {
    setIsHovered(false);
    onMouseOut(e);
  };

  // Using refs instead of checked attribute to support indeterminate checkbox state:
  // https://www.robinwieruch.de/react-checkbox-indeterminate/
  useEffect(() => {
    if (indeterminate) {
      checkboxRef.current.checked = false;
      checkboxRef.current.indeterminate = true;
    } else if (checked) {
      checkboxRef.current.checked = true;
      checkboxRef.current.indeterminate = false;
    } else {
      checkboxRef.current.checked = false;
      checkboxRef.current.indeterminate = false;
    }
  }, [checked, indeterminate]);

  return (
    <div style={style}>
      {contentBefore ? (
        <div id={`${id}-contentBefore`}>{contentBefore}</div>
      ) : null}
      <CheckboxRadioContainer className={className}>
        <HiddenInput
          type="checkbox"
          id={id}
          name={name}
          onBlur={onBlur}
          onChange={handleChange}
          onFocus={onFocus}
          onClick={handleClick}
          value={value}
          ref={checkboxRef}
          checked={checked}
          aria-checked={indeterminate ? 'mixed' : undefined}
          aria-describedby={`${contentBefore ? `${id}-contentBefore` : ''} ${
            contentAfter ? `${id}-contentAfter` : ''
          }`}
          disabled={disabled}
          {...props}
        />
        <StyledCheckboxRadioLabel
          facade={
            <StyledCheckboxRadioFacade
              type={indeterminate ? 'indeterminate' : 'checkbox'}
              shape={shape}
              isChecked={checked}
              size={size}
              isHovered={isHovered}
              isDisabled={disabled}
              color={color}
              borderColor={borderColor}
              checkedBackgroundColor={checkedBackgroundColor}
              checkmarkColor={checkmarkColor}
            />
          }
          htmlFor={id}
          content={label}
          isChecked={checked}
          isDisabled={disabled}
          size={size}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          layout="block"
        />
      </CheckboxRadioContainer>
      {contentAfter ? (
        <div id={`${id}-contentAfter`}>{contentAfter}</div>
      ) : null}
    </div>
  );
};

CCCheckbox.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  checked: PropTypes.bool,
  color: PropTypes.oneOf([undefined, 'success', 'caution', 'destroy']),
  borderColor: PropTypes.string,
  checkedBackgroundColor: PropTypes.string,
  uncheckedBackgroundColor: PropTypes.string,
  checkmarkColor: PropTypes.string,
  contentBefore: PropTypes.node,
  contentAfter: PropTypes.node,
  disabled: PropTypes.bool,
  indeterminate: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onMouseOver: PropTypes.func,
  onMouseOut: PropTypes.func,
  shape: PropTypes.oneOf(['square', 'circle']),
  size: PropTypes.number,
  value: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};
CCCheckbox.defaultProps = {
  checked: false,
  color: undefined,
  borderColor: undefined,
  checkedBackgroundColor: undefined,
  uncheckedBackgroundColor: undefined,
  checkmarkColor: undefined,
  contentBefore: undefined,
  contentAfter: undefined,
  disabled: false,
  indeterminate: false,
  shape: 'square',
  size: tokens.size_spacing_m,
  onChange: () => {},
  onClick: () => {},
  onFocus: () => {},
  onBlur: () => {},
  onMouseOver: () => {},
  onMouseOut: () => {},
};
export default CCCheckbox;
/* eslint-enable jsx-a11y/mouse-events-have-key-events */
